/**
 * ServiceAdvisorPieChart Component
 *
 * Pie/Donut chart showing labor gross distribution among service advisors.
 * Uses Recharts library (already in project).
 */

import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ServiceAdvisor } from './types';

interface ServiceAdvisorPieChartProps {
  advisors: ServiceAdvisor[];
  totalLaborGross: number;
}

/**
 * Color palette for pie slices
 * Using orange-based colors to match SERVICE department theme
 */
const COLORS = [
  '#f97316', // orange-500
  '#fb923c', // orange-400
  '#fdba74', // orange-300
  '#fed7aa', // orange-200
  '#2563eb', // blue-600
  '#3b82f6', // blue-500
  '#60a5fa', // blue-400
  '#93c5fd', // blue-300
  '#10b981', // emerald-500
  '#34d399', // emerald-400
  '#6ee7b7', // emerald-300
  '#a78bfa', // violet-400
];

/**
 * Format currency for display
 */
function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

/**
 * Custom tooltip component
 */
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
        <p className="font-semibold text-gray-900 mb-1 truncate">{data.name}</p>
        <p className="text-sm text-gray-600">
          Labor Gross:{' '}
          <span className="font-medium text-orange-600">
            {formatCurrency(data.value)}
          </span>
        </p>
        <p className="text-sm text-gray-600">
          Share: <span className="font-medium">{data.percentage.toFixed(1)}%</span>
        </p>
        <p className="text-sm text-gray-600">
          ROs: <span className="font-medium">{data.roCount}</span>
        </p>
      </div>
    );
  }
  return null;
};

/**
 * Custom legend formatter
 */
const renderLegendText = (value: string) => {
  // Truncate long names
  const maxLength = 15;
  if (value.length > maxLength) {
    return value.substring(0, maxLength) + '...';
  }
  return value;
};

export const ServiceAdvisorPieChart: React.FC<ServiceAdvisorPieChartProps> = ({
  advisors,
  totalLaborGross,
}) => {
  // Prepare chart data - limit to top advisors, group others
  const chartData = useMemo(() => {
    const MAX_SLICES = 8;

    if (advisors.length === 0 || totalLaborGross === 0) {
      return [];
    }

    // Sort by labor gross descending
    const sorted = [...advisors].sort(
      (a, b) => b.laborGrossTotal - a.laborGrossTotal
    );

    if (sorted.length <= MAX_SLICES) {
      return sorted.map((advisor, index) => ({
        name: advisor.advisorName,
        value: advisor.laborGrossTotal,
        percentage: (advisor.laborGrossTotal / totalLaborGross) * 100,
        roCount: advisor.roCountTotal,
        color: COLORS[index % COLORS.length],
      }));
    }

    // Group smaller advisors into "Others"
    const top = sorted.slice(0, MAX_SLICES - 1);
    const others = sorted.slice(MAX_SLICES - 1);
    const othersTotal = others.reduce((sum, a) => sum + a.laborGrossTotal, 0);
    const othersRoCount = others.reduce((sum, a) => sum + a.roCountTotal, 0);

    const result = top.map((advisor, index) => ({
      name: advisor.advisorName,
      value: advisor.laborGrossTotal,
      percentage: (advisor.laborGrossTotal / totalLaborGross) * 100,
      roCount: advisor.roCountTotal,
      color: COLORS[index % COLORS.length],
    }));

    if (othersTotal > 0) {
      result.push({
        name: `Others (${others.length})`,
        value: othersTotal,
        percentage: (othersTotal / totalLaborGross) * 100,
        roCount: othersRoCount,
        color: '#9ca3af', // gray-400
      });
    }

    return result;
  }, [advisors, totalLaborGross]);

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
        No data to display
      </div>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={75}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            formatter={renderLegendText}
            wrapperStyle={{ fontSize: '12px', paddingLeft: '10px' }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};
